// Bu yeni dosya PDF dönüşüm işlemini baştan sona ele alacak

import * as pdfjsLib from 'pdfjs-dist';

// PDF worker'ın doğru yüklendiğinden emin olmak için özel bir yapılandırma
const setupPdfWorker = async () => {
  // CDN üzerinden PDF.js worker dosyasını yükleme - doğru URL ve sürüm kullan
  const CDN_PDF_WORKER_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  const LOCAL_PDF_WORKER_URL = '/pdf.worker.min.js';
  
  // Worker URL'sini kontrol et ve ayarla
  if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
    console.log('PDF.js worker URL ayarlanıyor...');
    
    try {
      // Önce CDN URL'sini dene
      const response = await fetch(CDN_PDF_WORKER_URL, { method: 'HEAD' });
      if (response.ok) {
        pdfjsLib.GlobalWorkerOptions.workerSrc = CDN_PDF_WORKER_URL;
        console.log('PDF.js worker CDN üzerinden yüklendi');
      } else {
        // CDN başarısız olursa yerel dosyayı kullan
        pdfjsLib.GlobalWorkerOptions.workerSrc = LOCAL_PDF_WORKER_URL;
        console.log('PDF.js worker yerel dosyadan yükleniyor');
      }
    } catch (error) {
      console.warn('CDN kontrolü başarısız oldu, yerel dosyaya dönülüyor:', error);
      pdfjsLib.GlobalWorkerOptions.workerSrc = LOCAL_PDF_WORKER_URL;
    }
  }
  
  return true;
};

// PDF'ten görüntü oluşturma - tamamen yeniden yazıldı
export const convertPdfToImage = async (
  pdfFile: File, 
  outputFormat: 'jpeg' | 'png' = 'png',
  dpi: number = 300
): Promise<File> => {
  console.log(`PDF dönüştürme başlatılıyor: ${pdfFile.name}, ${dpi} DPI`);
  
  // Worker URL'sini ayarla
  await setupPdfWorker();
  
  try {
    // PDF dosyasını yükle
    const arrayBuffer = await pdfFile.arrayBuffer();
    console.log(`PDF dosyası okundu: ${arrayBuffer.byteLength} bytes`);
    
    // PDF dökümanını oluştur
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdfDoc = await loadingTask.promise;
    
    console.log(`PDF dökümanı yüklendi - ${pdfDoc.numPages} sayfa`);
    
    if (pdfDoc.numPages === 0) {
      throw new Error('PDF dosyasında sayfa bulunamadı');
    }
    
    // İlk sayfayı al
    const page = await pdfDoc.getPage(1);
    
    // PDF doğal DPI değeri (genellikle 72 dpi)
    const PDF_NATURAL_DPI = 72;
    const scale = dpi / PDF_NATURAL_DPI;
    
    // Viewport boyutlarını hesapla
    const viewport = page.getViewport({ scale });
    console.log(`Viewport hesaplandı: ${viewport.width}x${viewport.height} piksel`);
    
    // Canvas oluştur ve boyutlarını ayarla
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    
    // Canvas bağlamını al - alpha false ile daha iyi performans ve beyaz arka plan garantisi
    const ctx = canvas.getContext('2d', { alpha: false });
    
    if (!ctx) {
      throw new Error('Canvas 2D bağlamı oluşturulamadı');
    }
    
    // Arka planı beyaz yap (önemli!)
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Render ayarlarını yapılandır
    const renderContext = {
      canvasContext: ctx,
      viewport: viewport,
      intent: 'display' as const,
      background: 'white',
      renderInteractiveForms: true
    };
    
    // PDF sayfasını render et
    console.log('PDF sayfası render ediliyor...');
    const renderTask = page.render(renderContext);
    await renderTask.promise;
    console.log('PDF sayfa render işlemi tamamlandı');
    
    // Canvas içeriğini görüntüye dönüştür
    const imageBlob = await new Promise<Blob>((resolve, reject) => {
      try {
        if (outputFormat === 'jpeg') {
          // JPEG formatı için
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Blob oluşturulamadı'));
                return;
              }
              resolve(blob);
            },
            'image/jpeg',
            0.95 // Kalite
          );
        } else {
          // PNG formatı için
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Blob oluşturulamadı'));
                return;
              }
              resolve(blob);
            },
            'image/png'
          );
        }
      } catch (error) {
        reject(new Error(`Canvas to Blob hatası: ${error}`));
      }
    });
    
    console.log(`Görüntü oluşturuldu: ${imageBlob.size} bytes`);
    
    // Çıktı dosyasını oluştur
    const fileName = pdfFile.name.replace(/\.pdf$/i, `.${outputFormat}`);
    const outputFile = new File(
      [imageBlob],
      fileName,
      { type: outputFormat === 'jpeg' ? 'image/jpeg' : 'image/png' }
    );
    
    // Kaynakları temizle
    canvas.width = 0;
    canvas.height = 0;
    
    console.log(`PDF dönüştürme tamamlandı, dosya boyutu: ${outputFile.size} bytes`);
    
    // Yeterince büyük bir dosya mı? Beklenmedik şekilde küçük dosyalar genellikle hatalıdır
    if (outputFile.size < 1000) {
      console.warn('Oluşturulan dosya çok küçük, içeriği kontrol edin');
    }
    
    return outputFile;
    
  } catch (error) {
    console.error('PDF dönüştürme hatası:', error);
    throw new Error(`PDF dönüştürme başarısız: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
  }
};

// Alternatif dönüştürme yöntemi - PDF'in her sayfasını ayrı ayrı render eder
export const convertPdfWithFallback = async (
  pdfFile: File,
  outputFormat: 'jpeg' | 'png' = 'png',
  dpi: number = 300
): Promise<File> => {
  try {
    // Önce normal dönüştürme yöntemini dene
    return await convertPdfToImage(pdfFile, outputFormat, dpi);
  } catch (error) {
    console.warn('Standart dönüştürme başarısız, alternatif yöntem deneniyor:', error);
    
    // PDF.js worker URL'sini manuel olarak ayarla
    pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
    
    // PDF dosyasını yükle
    const arrayBuffer = await pdfFile.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdfDoc = await loadingTask.promise;
    
    // İlk sayfayı al
    const page = await pdfDoc.getPage(1);
    
    // Viewport ve scale hesapla
    const viewport = page.getViewport({ scale: dpi / 72 });
    
    // Doküman DOM'a eklenerek render edilir (farklı yaklaşım)
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    document.body.appendChild(tempDiv);
    
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    tempDiv.appendChild(canvas);
    
    // Canvas hazırlığı
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) {
      throw new Error('Canvas context oluşturulamadı');
    }
    
    // Arka planı beyaz yap
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Render işlemi
    await page.render({
      canvasContext: ctx,
      viewport
    }).promise;
    
    // Canvas to blob
    const imageBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Blob oluşturulamadı'));
            return;
          }
          resolve(blob);
        },
        outputFormat === 'jpeg' ? 'image/jpeg' : 'image/png',
        outputFormat === 'jpeg' ? 0.95 : undefined
      );
    });
    
    // DOM'dan oluşturduğumuz elemanları temizle
    document.body.removeChild(tempDiv);
    
    // Dosya oluştur
    const fileName = pdfFile.name.replace(/\.pdf$/i, `.${outputFormat}`);
    return new File(
      [imageBlob],
      fileName,
      { type: outputFormat === 'jpeg' ? 'image/jpeg' : 'image/png' }
    );
  }
};
